// AGC: Inputs/products multi-select — recommendation chips, category tabs, quantity inputs, auto-dose
import React, { useState } from 'react'
import { Check, Plus, ChevronDown, AlertTriangle } from 'lucide-react'
import { useTranslation } from '../../../hooks/useTranslation'
import { clampDecimals } from '../../../utils'
import type { VoiceFieldProps } from './types'

// AGC: Product entry from the catalog
interface CatalogProduct {
  id: string
  name: string
  category: string
  categoryLabel: string
  unit: string
  defaultQuantityPerHa?: number
  activeIngredient?: string
  currentStock?: number
  confidence?: number
  isRelevant?: boolean
}

// AGC: Selected input item (JSON-serialized in inputValues.selectedInputs)
interface SelectedInput {
  inputId: string
  name: string
  quantity: number
  unit: string
  category: string
}

// AGC: Category filter tab shape from backend
interface CategoryFilter {
  key: string
  label: string
  isRelevant: boolean
  count: number
}

export const InputsSelector: React.FC<VoiceFieldProps> = ({
  info,
  inputValues,
  setInputValues,
  lastResponse,
  wizardMode,
}) => {
  const { t } = useTranslation()
  // AGC: Toggle between compact chips view and full category-filtered list
  const [expandedInputs, setExpandedInputs] = useState(false)

  const allProducts: CatalogProduct[] = info.catalog || []
  // AGC: Backend already sets confidence: 95 (transcript-matched), 85 (category-relevant), 40 (other)
  // AGC: All products with confidence >= 80 are recommended — no frontend re-filtering needed
  const recommended = allProducts.filter(p => (p.confidence || 0) >= 80).slice(0, 8)

  // AGC: Parse selected inputs
  let selectedInputs: SelectedInput[] = []
  try { selectedInputs = JSON.parse(inputValues.selectedInputs || '[]') } catch { /* empty */ }
  const selectedIds = new Set(selectedInputs.map(s => s.inputId))

  const toggleProduct = (p: CatalogProduct) => {
    let updated: SelectedInput[]
    if (selectedIds.has(p.id)) {
      updated = selectedInputs.filter(s => s.inputId !== p.id)
    } else {
      updated = [...selectedInputs, {
        inputId: p.id,
        name: p.name,
        quantity: p.defaultQuantityPerHa || 1,
        unit: p.unit,
        category: p.category,
      }]
    }
    setInputValues(prev => ({ ...prev, selectedInputs: JSON.stringify(updated) }))
  }

  // AGC: Sizing classes — bigger in wizard mode to fill the full-screen card layout
  const sz = wizardMode
    ? { card: 'p-5', label: 'text-base', chip: 'px-4 py-2.5 text-base', checkIcon: 'w-5 h-5' }
    : { card: 'p-2', label: 'text-xs', chip: 'px-2.5 py-1 text-xs', checkIcon: 'w-3 h-3' }

  return (
    <div className={`${sz.card} bg-blue-500/5 border border-blue-500/20 rounded-lg`}>
      <div className="mb-1.5">
        <span className={`text-blue-300 ${sz.label} font-medium flex items-center gap-1`}>
          📦 {info.fieldLabel}
        </span>
        <p className="text-white/40 text-xs mt-0.5">{info.reason}</p>
      </div>

      {/* AGC: Phase 8B — Product recommendation chips (compact view) */}
      <div className="mb-2">
        <div className="flex flex-wrap gap-1.5">
          {/* AGC: Top recommendation chips with out-of-stock badge */}
          {recommended.map((p) => {
            const isSelected = selectedIds.has(p.id)
            const isOutOfStock = p.currentStock !== undefined && p.currentStock <= 0
            return (
              <button
                key={p.id}
                onClick={() => toggleProduct(p)}
                className={`inline-flex items-center gap-1 ${sz.chip} rounded-full transition-colors ${
                  isSelected
                    ? 'bg-blue-500/25 border border-blue-500/50 text-blue-300 font-medium'
                    : 'bg-blue-500/8 border border-blue-500/20 text-blue-200/80 hover:bg-blue-500/15'
                }`}
              >
                {isSelected && <Check className={sz.checkIcon} />}
                <span className="truncate max-w-[120px]">{p.name}</span>
                {/* AGC: Risk badge — warning icon for out of stock */}
                {isOutOfStock && (
                  <span title={t('voice.outOfStock')}><AlertTriangle className="w-3 h-3 text-red-400 flex-shrink-0" /></span>
                )}
                {/* AGC: Auto-dose suggestion — show default dose per hectare */}
                {isSelected && p.defaultQuantityPerHa && (
                  <span className="text-blue-300/60 text-[10px]">
                    ({p.defaultQuantityPerHa} {p.unit}/ha)
                  </span>
                )}
              </button>
            )
          })}

          {/* AGC: "+Mais" / "Ver todos" expand button — always visible */}
          {allProducts.length > 0 && (
            <button
              onClick={() => setExpandedInputs(prev => !prev)}
              className="inline-flex items-center gap-0.5 px-2 py-1 rounded-full text-xs text-white/50 hover:text-white/80 bg-white/5 hover:bg-white/10 transition-colors"
            >
              <Plus className="w-3 h-3" />
              {recommended.length > 0
                ? `${t('voice.moreOptions')} (${allProducts.length - recommended.length})`
                : `${t('voice.viewAll')} (${allProducts.length})`
              }
              <ChevronDown className={`w-3 h-3 transition-transform ${expandedInputs ? 'rotate-180' : ''}`} />
            </button>
          )}
        </div>
      </div>

      {/* AGC: Expanded full product list — shown when "+Mais"/"Ver todos" is clicked */}
      {expandedInputs && (
        <>
          {/* AGC: Category filter tabs - horizontal scrollable with "Recomendados" first */}
          <div className="flex gap-1 overflow-x-auto pb-1 mb-1.5 scrollbar-thin">
            {/* AGC: Recommended tab - shows products with confidence >= 80 */}
            {(() => {
              const recommendedCount = allProducts.filter(p => (p.confidence || 0) >= 80).length
              return recommendedCount > 0 ? (
                <button
                  onClick={() => setInputValues(prev => ({ ...prev, _inputCategoryFilter: '_recommended' }))}
                  className={`flex-shrink-0 px-2 py-1 rounded text-xs transition-colors whitespace-nowrap ${
                    inputValues._inputCategoryFilter === '_recommended'
                      ? 'bg-emerald-500/30 text-emerald-300 font-medium'
                      : 'bg-emerald-500/10 text-emerald-300/80 hover:bg-emerald-500/20'
                  }`}
                >
                  ⭐ {t('voice.recommended')} ({recommendedCount})
                </button>
              ) : null
            })()}

            {/* AGC: All products tab */}
            <button
              onClick={() => setInputValues(prev => ({ ...prev, _inputCategoryFilter: '' }))}
              className={`flex-shrink-0 px-2 py-1 rounded text-xs transition-colors ${
                !inputValues._inputCategoryFilter || inputValues._inputCategoryFilter === ''
                  ? 'bg-blue-500/30 text-blue-300 font-medium'
                  : 'bg-white/5 text-white/60 hover:bg-white/10'
              }`}
            >
              {t('voice.allCategories')}
            </button>

            {/* AGC: Individual category tabs - highlight relevant ones from backend scoring */}
            {(info.categoryFilters || []).map((cat: CategoryFilter) => {
              const selectedTaskCategory = inputValues.category || lastResponse?.parsedData?.category || ''
              const isDynamicallyRelevant = cat.isRelevant
              return (
                <button
                  key={cat.key}
                  onClick={() => setInputValues(prev => ({ ...prev, _inputCategoryFilter: cat.key }))}
                  className={`flex-shrink-0 px-2 py-1 rounded text-xs transition-colors whitespace-nowrap ${
                    inputValues._inputCategoryFilter === cat.key
                      ? 'bg-blue-500/30 text-blue-300 font-medium'
                      : isDynamicallyRelevant && selectedTaskCategory
                        ? 'bg-emerald-500/10 text-emerald-300/80 hover:bg-emerald-500/20'
                        : 'bg-white/5 text-white/60 hover:bg-white/10'
                  }`}
                >
                  {cat.label} ({cat.count})
                </button>
              )
            })}
          </div>

          {/* AGC: Product list - filtered by selected category or "recommended" */}
          <div className="space-y-1 max-h-32 overflow-y-auto mb-1.5">
            {(() => {
              const filter = inputValues._inputCategoryFilter || ''
              let filtered: CatalogProduct[]
              if (filter === '_recommended') {
                // AGC: Recommended = all products scored >= 80 by backend
                filtered = allProducts.filter(p => (p.confidence || 0) >= 80)
              } else if (filter) {
                // AGC: Filter by specific category
                filtered = allProducts.filter(p => p.category === filter)
              } else {
                // AGC: Show all products
                filtered = allProducts
              }
              // AGC: Re-parse selected inputs for current state
              let selected: SelectedInput[] = []
              try { selected = JSON.parse(inputValues.selectedInputs || '[]') } catch { /* empty */ }
              const selIds = new Set(selected.map(s => s.inputId))

              return filtered.length > 0 ? filtered.map((product) => {
                const isSelected = selIds.has(product.id)
                return (
                  <div
                    key={product.id}
                    className={`flex items-center gap-1.5 px-2 py-1.5 rounded text-xs transition-colors ${
                      isSelected
                        ? 'bg-emerald-500/20 border border-emerald-500/40'
                        : 'bg-white/5 hover:bg-white/10 border border-transparent'
                    }`}
                  >
                    <button
                      onClick={() => toggleProduct(product)}
                      className="flex-shrink-0"
                    >
                      {isSelected ? (
                        <Check className="w-3.5 h-3.5 text-emerald-400" />
                      ) : (
                        <div className="w-3.5 h-3.5 rounded border border-white/30" />
                      )}
                    </button>
                    <div className="flex-1 min-w-0">
                      <span className={`truncate block ${isSelected ? 'text-emerald-300' : 'text-white/80'}`}>
                        {product.name}
                      </span>
                      <span className="text-white/40 text-[10px]">
                        {product.categoryLabel} · {product.unit}
                        {product.activeIngredient ? ` · ${product.activeIngredient}` : ''}
                      </span>
                    </div>
                    {/* AGC: Phase 8B — Risk badge for out-of-stock products */}
                    {product.currentStock !== undefined && product.currentStock <= 0 && (
                      <span title={t('voice.outOfStock')}><AlertTriangle className="w-3 h-3 text-red-400 flex-shrink-0 mr-1" /></span>
                    )}
                    {isSelected && (
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <input
                          type="number"
                          min="0.1"
                          step="0.1"
                          value={selected.find(s => s.inputId === product.id)?.quantity || 1}
                          onChange={(e) => {
                            const qty = clampDecimals(parseFloat(e.target.value) || 0)
                            const upd = selected.map(s =>
                              s.inputId === product.id ? { ...s, quantity: qty } : s
                            )
                            setInputValues(prev => ({ ...prev, selectedInputs: JSON.stringify(upd) }))
                          }}
                          className="w-14 px-1 py-0.5 bg-white/10 border border-white/20 rounded text-white text-xs text-center focus:outline-none focus:border-emerald-500"
                        />
                        <span className="text-white/50 text-[10px]">{product.unit}</span>
                      </div>
                    )}
                  </div>
                )
              }) : (
                <p className="text-white/40 text-xs text-center py-2">
                  {t('voice.noProductsInCategory')}
                </p>
              )
            })()}
          </div>
        </>
      )}

      {/* AGC: Selected products summary — ALWAYS visible even when list is collapsed */}
      {(() => {
        let selected: SelectedInput[] = []
        try { selected = JSON.parse(inputValues.selectedInputs || '[]') } catch { /* empty */ }
        if (selected.length === 0) return null
        return (
          <div className="mt-1.5 pt-1.5 border-t border-white/10">
            <span className="text-emerald-300 text-[10px] font-medium">
              ✅ {selected.length} {t('voice.productsSelected')}:
            </span>
            <div className="mt-0.5 flex flex-wrap gap-1">
              {selected.map((s) => (
                <span key={s.inputId} className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-emerald-500/20 rounded text-emerald-300 text-[10px]">
                  {s.name}: {s.quantity} {s.unit}
                  <button
                    onClick={() => {
                      const upd = selected.filter(x => x.inputId !== s.inputId)
                      setInputValues(prev => ({ ...prev, selectedInputs: JSON.stringify(upd) }))
                    }}
                    className="ml-0.5 text-emerald-300/60 hover:text-red-400"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          </div>
        )
      })()}

      {/* AGC: Phase 8B — Auto-dose suggestion banner */}
      {/* AGC: When a product is selected and has defaultQuantityPerHa, show dosage recommendation */}
      {(() => {
        let selected: SelectedInput[] = []
        try { selected = JSON.parse(inputValues.selectedInputs || '[]') } catch { /* empty */ }
        const fieldArea = lastResponse?.parsedData?.area as number | undefined
        if (selected.length === 0 || !fieldArea) return null
        // AGC: Find products with default dose from catalog
        const productsWithDose = selected.filter(s => {
          const catalogEntry = allProducts.find(p => p.id === s.inputId)
          return catalogEntry?.defaultQuantityPerHa
        })
        if (productsWithDose.length === 0) return null
        return (
          <div className="mt-1.5 p-2 bg-blue-500/10 border border-blue-500/25 rounded-lg">
            <span className="text-blue-300 text-[10px] font-medium">
              💡 {t('voice.autoDoseSuggestion')} ({fieldArea} ha):
            </span>
            <div className="mt-0.5 space-y-0.5">
              {productsWithDose.map(s => {
                const catalogEntry = allProducts.find(p => p.id === s.inputId)
                const dosePH = catalogEntry?.defaultQuantityPerHa || 0
                const totalDose = Math.round(dosePH * fieldArea * 100) / 100
                return (
                  <div key={s.inputId} className="text-blue-200/70 text-[10px]">
                    {s.name}: {dosePH} {s.unit}/ha × {fieldArea} ha = <strong className="text-blue-300">{totalDose} {s.unit}</strong>
                  </div>
                )
              })}
            </div>
          </div>
        )
      })()}
    </div>
  )
}
